/**
 * Drawer Service (drawerService.js)
 *
 * Provides a reactive service for managing the HTML content and lifecycle events of the drawer UI component.
 * Uses an RxJS Subject to emit and observe drawer-related actions across the application.
 *
 * Features:
 * - clearHtml: Emits an action to clear the drawer's HTML content.
 * - getHtml: Returns an observable stream of all drawer actions for subscription.
 * - onHtmlReady: Emits an action when the drawer's HTML is ready and inserted into the DOM.
 * - onHtmlRemoved: Emits an action when the drawer's HTML is removed from the DOM.
 * - setHtml: Emits an action to set or update the drawer's HTML content.
 *
 * This service enables decoupled communication between the drawer component and other parts of the application,
 * supporting a reactive, event-driven UI architecture.
 */

import { Subject } from 'rxjs';

const subject = new Subject();

export default {
  /**
   * Clears the HTML content of the drawer.
   */
  clearHtml: () => subject.next({ action: 'clearHtml' }),

  /**
   * Returns an observable stream of all drawer actions.
   */
  getHtml: () => subject.asObservable(),

  /**
   * Registers a callback to be invoked when the drawer's HTML is ready.
   */
  onHtmlReady: (data) => subject.next({ action: 'onHtmlReady', ...data }),

  /**
   * Emits an action when the drawer's HTML is removed from the DOM.
   */
  onHtmlRemoved: (data) => subject.next({ action: 'onHtmlRemoved', ...data }),

  /**
   * Sets the HTML content of the drawer.
   */
  setHtml: (data) => subject.next({ action: 'setHtml', value: data.payload }),

  /**
   * Mounts a live DOM element into the drawer, preserving event listeners.
   * @param {HTMLElement} element
   * @param {{ peek?: string }} [options] - Optional strategy options.
   *   `peek`: height string (e.g. `'72px'`). When set, the drawer starts in
   *   `--collapsed` state showing that height; swipe-down snaps back to it
   *   instead of closing; tap expands to `--open`. (Strategy Pattern)
   */
  mountElement: (element, options = {}) =>
    subject.next({ action: 'mountElement', value: element, options }),
};
